import { Link } from 'react-router-dom';
import { Logo } from '@/components/brand/Logo';

/**
 * The branded Coming Soon experience — BUILD-PLAN §23, §60, §66.
 *
 * This is the single most important component for the launch strategy: it is
 * what every disabled module renders instead of a broken page, a blank screen
 * or a 404. §60 is explicit that it "must look intentional and premium — it
 * should never look like an unfinished website", so it is a deliberately
 * composed editorial page rather than a placeholder.
 */

interface ComingSoonProps {
  /** Feature-specific heading; falls back to the brand default. */
  title?: string | null;
  description?: string | null;
  /** Optional editorial image. Omitted rather than shown as a grey box (§74). */
  image?: string | null;
  /** Machine name, surfaced only for support/debugging context. */
  featureName?: string | null;
  backButton?: boolean;
  /**
   * Notify-me capture. Only rendered when a handler is supplied — §86 forbids
   * a button that pretends to work, so no handler means no form.
   */
  notifyMe?: (() => void) | undefined;
}

const DEFAULT_TITLE = 'A Little More Luxe Is Coming';
const DEFAULT_DESCRIPTION =
  "We're carefully finishing this experience so it feels as beautiful as everything we create.";

export function ComingSoon({
  title,
  description,
  image,
  featureName,
  backButton = true,
  notifyMe,
}: ComingSoonProps) {
  return (
    <section
      className="flex min-h-[70vh] flex-col items-center justify-center px-6 py-16 text-center"
      aria-labelledby="coming-soon-title"
    >
      <Logo size="lg" decorative />

      <h1
        id="coming-soon-title"
        className="mt-10 max-w-2xl font-display text-3xl leading-tight text-ink sm:text-4xl"
      >
        {title || DEFAULT_TITLE}
      </h1>

      <p className="mt-5 max-w-xl text-base leading-relaxed text-muted">
        {description || DEFAULT_DESCRIPTION}
      </p>

      {image && (
        <img
          src={image}
          alt=""
          loading="lazy"
          className="mt-10 max-h-72 w-full max-w-lg rounded-md object-cover shadow-card"
        />
      )}

      {/* The brand promise, set as the signature it is in the identity. */}
      <p className="mt-12 font-display text-lg italic leading-snug text-accent">
        Made with Purpose.
        <br />
        Packaged with Love.
      </p>

      <div className="mt-10 flex flex-col gap-3 sm:flex-row">
        {backButton && (
          <Link
            to="/"
            className="inline-flex min-h-11 items-center justify-center rounded-sm border border-ink px-8 text-sm font-medium tracking-wide text-ink transition-colors hover:bg-ink hover:text-white"
          >
            Back to Home
          </Link>
        )}

        {notifyMe && (
          <button
            type="button"
            onClick={notifyMe}
            className="inline-flex min-h-11 items-center justify-center rounded-sm bg-accent px-8 text-sm font-medium tracking-wide text-white transition-opacity hover:opacity-90"
          >
            Notify Me
          </button>
        )}
      </div>

      {/*
        Kept out of the accessibility tree and visually quiet: useful when a
        customer reports an issue, but not part of the brand experience.
      */}
      {featureName && (
        <p className="mt-8 text-xs text-muted opacity-50" aria-hidden="true">
          {featureName}
        </p>
      )}
    </section>
  );
}
